import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Award, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';

function Reveal({ children, delay = 0, className = '' }) {
  const { ref, isVisible } = useScrollReveal(0.1);
  return (
    <div
      ref={ref}
      className={`${isVisible ? 'reveal-visible' : 'reveal-hidden'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ─── About Hero ─── */
function AboutHero() {
  return (
    <section className="bg-brand-navy py-20 lg:py-28" data-testid="about-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <p className="text-brand-orange font-semibold text-sm uppercase tracking-widest mb-4 font-source-sans">
              About iMANE
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-lexend font-bold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-tight mb-6">
              Boutique by design.{' '}
              <br className="hidden sm:block" />
              <span className="text-brand-orange">Expert by pedigree.</span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed font-source-sans">
              iMANE was founded in 2022 by senior procurement technology
              practitioners who recognised a persistent gap in the market:
              enterprises deserved deep, specialised supply chain and procurement expertise - not
              broad ERP generalism delivered through layers of junior resource.
            </p>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed font-source-sans mt-4">
              Spun off from an established SAP partner firm, we were built from
              day one with a single, deliberate purpose - to do one thing
              exceptionally well.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ─── Company Story ─── */
function CompanyStory() {
  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="company-story">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <Reveal>
              <h2 className="font-lexend font-bold text-3xl lg:text-4xl text-brand-navy tracking-tight mb-6">
                Our Story
              </h2>
            </Reveal>
            <Reveal delay={80}>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed font-source-sans">
                What began as a conviction that senior-led, boutique delivery
                outperforms large-firm generalism has grown into a team of
                26-50 certified specialists, serving clients across
                India, APAC, Southeast Asia, and Australia/NZ.
              </p>
            </Reveal>
            <Reveal delay={160}>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed mt-5 font-source-sans">
                Every engagement is led by practitioners who have spent their
                careers in source-to-pay. You work directly with people who
                understand the nuance - not generalists learning on your
                budget, and not junior teams managed from a distance.
              </p>
            </Reveal>
          </div>
          <Reveal delay={200}>
            <div
              className="w-full h-80 lg:h-96 rounded-2xl overflow-hidden border border-brand-border bg-white"
              data-testid="company-story-photo"
            >
              <img
                src="/office/office-1.jpg"
                alt="iMANE office"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ─── Founder Highlights (Stats + Pillars) ─── */
function FounderHighlights() {
  const stats = [
    { value: '2022', label: 'Founded by senior practitioners' },
    { value: '26-50', label: 'Certified supply chain & procurement specialists' },
    { value: '15+', label: 'Countries served: India, Singapore, Malaysia, Philippines, UAE, Australia, Canada & more' },
  ];
  const pillars = [
    {
      title: 'Certified depth, not breadth',
      body: 'Every team member is a dedicated specialist - not a generalist ERP consultant rotating between platforms.',
    },
    {
      title: 'Senior-led, always',
      body: 'The people who scope your engagement are the people who deliver it. No handoffs to junior teams.',
    },
    {
      title: 'One focus',
      body: "Source-to-pay is all we do - which means we have seen nearly every configuration, challenge, and edge case you might face.",
    },
    {
      title: 'Enterprise-grade trust',
      body: 'Our roots are in a leading SAP partner firm, giving us institutional rigour with the responsiveness of a specialist boutique.',
    },
  ];

  return (
    <section className="py-16 lg:py-20 bg-brand-orange border-t border-brand-navy/20" data-testid="founder-highlights">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Stat row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-14">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 80}>
              <div
                className="bg-brand-bg rounded-xl p-7 border border-brand-border text-center"
                data-testid={`founder-stat-${i + 1}`}
              >
                <div className="font-jetbrains text-3xl lg:text-4xl font-bold text-brand-orange mb-2 leading-none">
                  {s.value}
                </div>
                <p className="text-slate-700 text-sm font-source-sans">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Trust pillars */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={i * 70}>
              <div
                className="bg-brand-bg rounded-xl p-7 border border-brand-border hover:border-brand-orange transition-colors duration-200 h-full"
                data-testid={`trust-pillar-${i + 1}`}
              >
                <h3 className="font-lexend font-semibold text-lg text-brand-navy mb-2">
                  {p.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed font-source-sans">
                  {p.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Founding Leadership ─── */
function FoundingLeadership() {
  const founders = [
    {
      title: 'Co-Founder',
      bio: "Our first co-founder brings experience across labour law and structured governance, contributing to a strong culture of compliance, process excellence, and institutional direction that underpins everything iMANE does.",
    },
    {
      title: 'Co-Founder',
      bio: 'Our second co-founder brings a background spanning defence, government organisations, and architectural planning. This experience contributes to structured execution, operational rigour, and the enterprise-grade trust that iMANE is built on.',
    },
  ];

  const officePhotos = [
    '/office/office-1.jpg',
    '/office/office-2.jpg',
    '/office/office-3.jpg',
    '/office/office-4.jpg',
    '/office/office-5.jpg',
    '/office/office-6.jpg',
    '/office/office-7.jpg',
    '/office/office-8.jpg',
  ];

  const [current, setCurrent] = useState(0);

  const prev = useCallback(() => {
    setCurrent((c) => (c - 1 + officePhotos.length) % officePhotos.length);
  }, [officePhotos.length]);

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % officePhotos.length);
  }, [officePhotos.length]);

  // Auto-advance every 4 seconds
  useEffect(() => {
    const id = setInterval(next, 4000);
    return () => clearInterval(id);
  }, [next]);

  return (
    <section className="py-20 lg:py-28 bg-brand-navy" data-testid="founding-leadership">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center mb-12">
          <h2 className="font-lexend font-bold text-3xl lg:text-4xl text-white tracking-tight">
            Founding Leadership
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-10">
          {founders.map((f, i) => (
            <Reveal key={i} delay={i * 100}>
              <div
                className="bg-white rounded-xl p-8 border border-brand-border h-full hover:border-brand-orange transition-colors duration-200"
                data-testid={`founder-card-${i + 1}`}
              >
                <p className="text-brand-orange font-semibold text-sm mb-4 font-source-sans uppercase tracking-wider">
                  {f.title}
                </p>
                <p className="text-slate-600 text-base leading-relaxed font-source-sans">
                  {f.bio}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={150}>
          <p className="text-slate-300 text-base leading-relaxed font-source-sans max-w-3xl mx-auto text-center italic mb-14">
            While the next-generation leadership actively drives business growth
            and procurement technology delivery, the founders continue to provide strategic
            guidance and institutional direction to the organisation.
          </p>
        </Reveal>

        {/* Office Photo Carousel */}
        <Reveal delay={200}>
          <h3 className="font-lexend font-semibold text-xl text-white text-center mb-6">
            At iMANE
          </h3>
          <div className="relative" data-testid="office-photo-gallery">
            {/* Main photo */}
            <div className="relative overflow-hidden rounded-2xl border border-slate-700 bg-slate-800 aspect-[16/9] max-w-3xl mx-auto">
              {officePhotos.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt={`At iMANE office ${i + 1}`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                  style={{ opacity: i === current ? 1 : 0 }}
                  data-testid={`office-photo-${i + 1}`}
                />
              ))}

              {/* Prev button */}
              <button
                onClick={prev}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-brand-orange text-white flex items-center justify-center transition-colors duration-200 z-10"
                aria-label="Previous photo"
                data-testid="gallery-prev-btn"
              >
                <ChevronLeft size={20} />
              </button>

              {/* Next button */}
              <button
                onClick={next}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-brand-orange text-white flex items-center justify-center transition-colors duration-200 z-10"
                aria-label="Next photo"
                data-testid="gallery-next-btn"
              >
                <ChevronRight size={20} />
              </button>

              {/* Counter badge */}
              <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-source-sans px-2.5 py-1 rounded-full z-10">
                {current + 1} / {officePhotos.length}
              </div>
            </div>

            {/* Dot indicators */}
            <div className="flex justify-center gap-2 mt-5">
              {officePhotos.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${i === current ? 'bg-brand-orange w-6' : 'bg-slate-600 hover:bg-slate-400'}`}
                  aria-label={`Go to photo ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── Team Overview ─── */
function TeamOverview() {
  const stats = [
    { value: '26–50', label: 'Team members globally', icon: Users },
    { value: 'Multiple', label: 'Active SAP certifications', icon: Award },
    { value: '30+', label: 'Clients served across India, APAC & ANZ', icon: MapPin },
  ];

  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="team-overview">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <Reveal>
            <h2 className="font-lexend font-bold text-3xl lg:text-4xl text-brand-navy tracking-tight mb-5">
              A Team Built Around One Discipline
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed font-source-sans">
              With 26-50 practitioners across delivery, strategy, and managed
              services - all specialising in global ERP, supply chain domain experience,
              and technology - iMANE operates at a
              scale that allows genuine depth without the dilution that comes
              with size. Multiple team members hold active certifications
              across the suite.
            </p>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 80}>
              <div
                className="stat-card text-center p-8 bg-brand-bg rounded-xl border border-brand-border"
                data-testid={`team-stat-${i + 1}`}
              >
                <s.icon size={28} className="text-brand-orange mx-auto mb-4" />
                <div className="font-jetbrains text-4xl font-bold text-brand-orange mb-3">
                  {s.value}
                </div>
                <p className="text-slate-600 text-sm font-source-sans">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Global Delivery Map ─── */
function GlobalDeliveryMap() {
  const countries = [
    { name: 'India (HQ - Pune)' },
    { name: 'Singapore' },
    { name: 'Malaysia' },
    { name: 'Philippines' },
    { name: 'UAE (Abu Dhabi)' },
    { name: 'Australia' },
    { name: 'New Zealand' },
    { name: 'Canada' },
    { name: 'United Kingdom' },
    { name: 'United States' },
    { name: 'Germany' },
    { name: 'Japan' },
  ];

  return (
    <section className="py-20 lg:py-28 bg-brand-orange" data-testid="global-map">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Reveal>
            <h2 className="font-lexend font-bold text-3xl lg:text-4xl text-brand-navy tracking-tight mb-4">
              One Office. Global Delivery.
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="text-brand-navy text-base font-source-sans">
              Headquartered in Wakad, Pune, Maharashtra. iMANE delivers
              across India, Southeast Asia, the Middle East, Oceania, North America, and Europe.
              Our delivery model is designed for precision, not proximity.
            </p>
          </Reveal>
        </div>

        <Reveal delay={120}>
          <div className="bg-brand-navy rounded-2xl overflow-hidden p-8" data-testid="world-map-graphic">
            <div className="relative bg-slate-800 rounded-xl p-8 min-h-64">
              {/* Background watermark */}
              <div className="absolute inset-0 flex items-center justify-center opacity-5">
                <div className="text-white text-9xl font-bold tracking-widest select-none">GLOBAL</div>
              </div>

              <div className="relative grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {countries.map((c) => (
                  <div
                    key={c.name}
                    className="flex items-center gap-2.5 bg-slate-700 rounded-lg px-3 py-2.5"
                  >
                    <div className="w-2.5 h-2.5 rounded-full bg-brand-orange flex-shrink-0 animate-pulse" />
                    <span className="text-white text-sm font-medium font-source-sans">{c.name}</span>
                  </div>
                ))}
              </div>

              <div className="mt-5 text-center">
                <p className="text-slate-400 text-xs font-source-sans">
                  India · Singapore · Malaysia · Philippines · UAE · Australia · New Zealand · Canada · UK · USA · Germany · Japan
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <div data-testid="about-page">
      <Helmet>
        <title>About iMANE | Mane Labs LLP - Boutique Procurement Consulting Firm</title>
        <meta name="description" content="iMANE, by Mane Labs LLP, is a boutique procurement and supply chain consulting firm founded in 2022. 26-50 certified specialists. 15+ countries. Senior-led always." />
        <meta name="keywords" content="iMANE about, mane labs LLP about, about mane labs, iMANE company, procurement consulting firm India, SAP Ariba specialists, iMANE Pune" />
        <link rel="canonical" href="https://www.imane.in/about" />
        <meta property="og:title" content="About iMANE | Mane Labs LLP - Boutique Procurement Consulting Firm" />
        <meta property="og:description" content="Founded in 2022. 26-50 certified specialists. 15+ countries. Boutique procurement and supply chain consulting from Pune, India." />
        <meta property="og:url" content="https://www.imane.in/about" />
      </Helmet>
      <AboutHero />
      <CompanyStory />
      <FounderHighlights />
      <FoundingLeadership />
      <TeamOverview />
      <GlobalDeliveryMap />
    </div>
  );
}
